import { useNavigate } from "react-router-dom";
import { Scissors, BedDouble, ShoppingBag, Award, ArrowRight, Star } from "lucide-react";
import { Button } from "../../components/ui/button";

const FEATURES = [
  {
    icon: ShoppingBag, title: "Pet Supply Shop",
    text: "Premium kibble, treats, toys and essentials — shop online and pick up in store. Pay with GCash or cash.",
    img: "https://images.unsplash.com/photo-1568640347023-a616a30bc3bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxkb2clMjBjYXQlMjBmb29kJTIwdHJlYXRzfGVufDB8fHx8MTc4ODAzODYwMHww&ixlib=rb-4.1.0&q=85",
    cta: "Browse the shop", to: "/shop", accent: "bg-[#0E7C84]",
  },
  {
    icon: Scissors, title: "Grooming Studio",
    text: "Full grooming, bath & dry, express trims and spa treatments by certified groomers. Book a slot online.",
    img: "https://images.unsplash.com/photo-1625316708582-7c38734be31d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwZ3Jvb21pbmclMjBwZXQlMjBzaG9wfGVufDB8fHx8MTc4ODc5NjIzN3ww&ixlib=rb-4.1.0&q=85",
    cta: "Book grooming", to: "/book", accent: "bg-[#E84855]",
  },
  {
    icon: BedDouble, title: "Boarding Suites",
    text: "Cozy suites, deluxe kennels and cat condos with daily feeding, walks and activity logs you can check anytime.",
    img: "https://images.unsplash.com/photo-1546447147-3fc2b8181a74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxkb2clMjBob3RlbCUyMGJvYXJkaW5nJTIwcGV0JTIwY2FyZXxlbnwwfHx8fDE3ODg0NzQyNDd8MA&ixlib=rb-4.1.0&q=85",
    cta: "Reserve a suite", to: "/book", accent: "bg-[#2A9D8F]",
  },
  {
    icon: Award, title: "Paw Points Loyalty",
    text: "Earn 1 point for every ₱50 spent. Track pets, bookings and invoices from your own pet parent portal.",
    img: "https://images.unsplash.com/photo-1569203685368-118435c389dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxkb2clMjBob3RlbCUyMGJvYXJkaW5nJTIwcGV0JTIwY2FyZXxlbnwwfHx8fDE3ODg0NzQyNDd8MA&ixlib=rb-4.1.0&q=85",
    cta: "Create an account", to: "/register", accent: "bg-[#F4A261]",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-rise">
          <div className="inline-flex items-center gap-2 bg-[#D9F7F8] text-[#0E7C84] text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <Star className="w-3.5 h-3.5 fill-[#F4A261] text-[#F4A261]" /> Metro Manila's pet care hub
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1E293B] leading-[1.08]">
            Everything your pet needs, <span className="text-[#0E7C84]">under one tropical roof.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
            Shop quality supplies, book grooming with our certified stylists, and reserve cozy boarding
            suites — all synced in one place, from store counter to your phone.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="bg-[#0E7C84] hover:bg-[#093F46] rounded-full px-6" onClick={() => navigate("/book")} data-testid="hero-book-btn">
              Book an appointment <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-full px-6 border-[#0E7C84] text-[#0E7C84] hover:bg-[#D9F7F8]" onClick={() => navigate("/shop")} data-testid="hero-shop-btn">
              Shop supplies
            </Button>
          </div>
          <div className="mt-8 flex gap-8 text-sm">
            <div><p className="font-display font-bold text-2xl text-[#0E7C84]">500+</p><p className="text-slate-500">Happy pets served</p></div>
            <div><p className="font-display font-bold text-2xl text-[#0E7C84]">6</p><p className="text-slate-500">Boarding suites</p></div>
            <div><p className="font-display font-bold text-2xl text-[#0E7C84]">4.9</p><p className="text-slate-500">Customer rating</p></div>
          </div>
        </div>
        <div className="relative animate-rise" style={{ animationDelay: "120ms" }}>
          <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#F4A261]/30 rounded-full blur-2xl" />
          <img
            src="https://images.unsplash.com/photo-1625316708582-7c38734be31d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwZ3Jvb21pbmclMjBwZXQlMjBzaG9wfGVufDB8fHx8MTc4ODc5NjIzN3ww&ixlib=rb-4.1.0&q=85"
            alt="Dog being groomed"
            className="relative rounded-[2rem] object-cover w-full h-[420px] shadow-2xl"
          />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-[#D9F7F8] flex items-center justify-center">
              <Scissors className="w-5 h-5 text-[#0E7C84]" />
            </span>
            <div>
              <p className="text-xs text-slate-500">Next available slot</p>
              <p className="font-display font-semibold text-[#0E7C84]">Today, 2:00 PM</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">One roof, four ways to spoil them</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="bg-white rounded-2xl overflow-hidden border border-[#BFE8E9] hover-lift animate-rise" style={{ animationDelay: `${i * 90}ms` }} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="h-40 overflow-hidden">
                <img src={f.img} alt={f.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <span className={`inline-flex w-9 h-9 rounded-xl ${f.accent} items-center justify-center mb-3`}>
                  <f.icon className="w-4.5 h-4.5 w-5 h-5 text-white" />
                </span>
                <h3 className="font-display font-semibold text-lg">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.text}</p>
                <button onClick={() => navigate(f.to)} className="mt-4 text-sm font-semibold text-[#0E7C84] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`feature-cta-${i}`}>
                  {f.cta} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
